/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Dropdown, Flex, Modal } from "antd";
import {
  useDeleteSubcategoryMutation,
  useGetSubcategoriesQuery,
} from "../../../../app/features/admin/admin.api";
import type { Subcategory } from "../../../../app/features/admin/admin.types";
import ActionIcon from "../../../../assets/icons/ActionIcon";
import AddIcon from "../../../../assets/icons/AddIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../assets/icons/EditIcon";
import ViewIcon from "../../../../assets/icons/ViewIcon";
import CustomTable from "../../../../components/CustomTable";
import CustomTitle from "../../../../components/CustomTitle";
import StatusTag from "../../../../components/StatusTag";
import useFilters from "../../../../hooks/useFilters";
import useModal from "../../../../hooks/useModal";
import DashboardLayout from "../../../../layouts/DashboardLayout";
import { toast } from "../../../../lib/toast";
import type { ColumnsType } from "../../../../types/global.types";
import { countParPageSerialNumber } from "../../../../utils/global.utils";
import SubcategoryDetails from "./_components/SubcategoryDetails";
import SubcategoryModal from "./_components/SubcategoryModal";

const Subcategories = () => {
  const {
    page,
    limit,
    handleLimit,
    // handleSearch,
    // handleFilter,
    handlePagination,
  } = useFilters<{
    status?: string | undefined;
    search?: string | undefined;
  }>();
  const { open, handleOpen, handleClose } = useModal<Subcategory>();
  const { data, isLoading } = useGetSubcategoriesQuery();

  const [deleteSubcategory] = useDeleteSubcategoryMutation();

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this subcategory?",
      content: "This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteSubcategory({ id }).unwrap();
          toast("success", "Subcategory deleted successfully");
        } catch (error: any) {
          toast(
            "error",
            error?.data?.message || "Failed to delete subcategory",
          );
        }
      },
    });
  };

  const statusOptions = [
    { label: "Active", value: "true", className: "status-success" },
    { label: "Inactive", value: "false", className: "status-error" },
  ];

  const columns: ColumnsType<Subcategory> = [
    {
      key: "sl",
      title: "#SL",
      align: "center",
      width: 60,
      render: (_: any, __: any, index: number) =>
        countParPageSerialNumber(page, limit, index),
    },
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
    },
    {
      key: "category",
      title: "Category",
      dataIndex: ["Category", "name"],
      render: (_, record: Subcategory) => record.Category?.name || "N/A",
    },
    // {
    //   key: "slug",
    //   title: "Slug",
    //   dataIndex: "slug",
    // },
    {
      key: "sortOrder",
      title: "Sort Order",
      dataIndex: "sortOrder",
      align: "center",
    },
    {
      key: "isActive",
      title: "Status",
      dataIndex: "isActive",
      render: (isActive: boolean) => (
        <StatusTag value={isActive ? "true" : "false"} config={statusOptions} />
      ),
    },
    {
      key: "action",
      title: "Action",
      align: "end",
      width: 100,
      render: (_: any, record: Subcategory) => (
        <Dropdown
          overlayClassName="w-36"
          placement="bottomRight"
          menu={{
            items: [
              {
                key: "view",
                label: (
                  <Flex align="center" gap={8}>
                    <Flex justify="center" className="w-6 text-theme-sky-500">
                      <ViewIcon size={24} />
                    </Flex>
                    <span className="text-sm font-medium">View</span>
                  </Flex>
                ),
                onClick: () => handleOpen({ type: "VIEW", data: record }),
              },
              {
                key: "edit",
                label: (
                  <Flex align="center" gap={8}>
                    <Flex
                      justify="center"
                      className="w-6 text-theme-yellow-500"
                    >
                      <EditIcon size={18} />
                    </Flex>
                    <span className="text-sm font-medium">Edit</span>
                  </Flex>
                ),
                onClick: () => handleOpen({ type: "UPDATE", data: record }),
              },
              {
                key: "delete",
                label: (
                  <Flex align="center" gap={8}>
                    <Flex justify="center" className="w-6 text-theme-error-500">
                      <DeleteIcon size={16} />
                    </Flex>
                    <span className="text-sm font-medium">Delete</span>
                  </Flex>
                ),
                onClick: () => handleDelete(record?.id),
              },
            ],
          }}
          trigger={["click"]}
        >
          <Button
            type="text"
            size="small"
            icon={<ActionIcon />}
            classNames={{ icon: "!size-8 flex items-center justify-center" }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <DashboardLayout title="Subcategories">
      <CustomTitle
        title="Subcategory Management"
        description="Manage all course subcategories"
      >
        <Button
          onClick={() => handleOpen({ type: "ADD" })}
          className="btn-primary"
        >
          <AddIcon /> Add New Subcategory
        </Button>
      </CustomTitle>

      <CustomTable
        total={10}
        page={page}
        limit={limit}
        loading={isLoading}
        columns={columns}
        dataSource={Array.isArray(data) ? data : []}
        onLimitChange={handleLimit}
        onPaginate={handlePagination}
        tableTitle="All Subcategories"
        hideRowSelect
      />

      {(open?.type === "ADD" || open?.type === "UPDATE") && (
        <SubcategoryModal
          open={open?.open}
          onCancel={handleClose}
          initialValues={open?.data}
          type={open?.type}
        />
      )}

      {open?.type === "VIEW" && (
        <SubcategoryDetails
          open={open?.open}
          onCancel={handleClose}
          subcategory={open?.data}
          type={open?.type}
        />
      )}
    </DashboardLayout>
  );
};

export default Subcategories;
